/** Color conversions shared by the inspector color/gradient controls. */

/** The hex a `{ref:'custom'}` falls back to when nothing better is known. */
export const CUSTOM_FALLBACK_HEX = '#' + '7c3aed';

/**
 * The live hex behind a theme token (`--aqx-theme-<token>`), read off a PROBE element rather than the raw
 * custom property: half the theme vars are `color-mix(...)` expressions (border, muted, surface-2), which
 * the raw property value exposes verbatim — only a resolved `color` gives a usable rgb triple.
 *
 * Two callers need it, and for different reasons: the colour control, so switching a slot → "Custom…"
 * starts from the colour the merchant is looking at; and the Countdown design panel, whose campaign is
 * stored as HEX far from any theme (04/10/2026 — a theme slot picked there used to write nothing at all,
 * so nine of the eleven entries in the dropdown silently did nothing).
 */
export function themeTokenHex(token: string): string {
  if (typeof document === 'undefined' || !document.body) return CUSTOM_FALLBACK_HEX;
  const probe = document.createElement('span');
  probe.style.cssText = `position:absolute;opacity:0;pointer-events:none;color:var(--aqx-theme-${token})`;
  document.body.appendChild(probe);
  const computed = getComputedStyle(probe).color;
  probe.remove();
  const m = /^rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/.exec(computed.trim());
  if (!m)
    return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(computed.trim())
      ? computed.trim()
      : CUSTOM_FALLBACK_HEX;
  const hex = [m[1], m[2], m[3]].map((c) => Number(c).toString(16).padStart(2, '0')).join('');
  return `#${hex}`;
}

export const clamp = (n: number, a: number, b: number) => Math.min(b, Math.max(a, n));

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

export function rgbToHex(r: number, g: number, b: number): string {
  const to = (n: number) =>
    Math.round(clamp(n, 0, 255))
      .toString(16)
      .padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

export function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  return { h: Math.round(h), s: Math.round(s * 100), v: Math.round(max * 100) };
}

export function hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  s /= 100;
  v /= 100;
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}

export function hexToHsv(hex: string): { h: number; s: number; v: number } | null {
  const rgb = hexToRgb(hex);
  return rgb ? rgbToHsv(rgb.r, rgb.g, rgb.b) : null;
}

export function hsvToHex(h: number, s: number, v: number): string {
  const { r, g, b } = hsvToRgb(h, s, v);
  return rgbToHex(r, g, b);
}
